/**
 * Request and routing metrics for ludicrous-gateway
 * Tracks performance and usage statistics
 */

import type { RoutingDecision } from "../types/routing.js";

/**
 * Per-provider metrics
 */
export interface ProviderMetrics {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalLatencyMs: number;
  streamingRequests: number;
  tokensSent: number;
  tokensReceived: number;
}

/**
 * Per-rule metrics
 */
export interface RuleMetrics {
  matchCount: number;
  lastMatchTime: Date | null;
}

/**
 * Overall gateway metrics
 */
export interface GatewayMetrics {
  startTime: Date;
  totalRequests: number;
  activeRequests: number;
  providers: Map<string, ProviderMetrics>;
  rules: Map<string, RuleMetrics>;
}

/**
 * Metrics collector class
 */
export class MetricsCollector {
  private startTime: Date;
  private totalRequests: number = 0;
  private activeRequests: number = 0;
  private providers: Map<string, ProviderMetrics> = new Map();
  private rules: Map<string, RuleMetrics> = new Map();

  constructor() {
    this.startTime = new Date();
  }

  /**
   * Get or create metrics for a provider
   */
  private getProviderMetrics(provider: string): ProviderMetrics {
    let metrics = this.providers.get(provider);
    if (!metrics) {
      metrics = {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        totalLatencyMs: 0,
        streamingRequests: 0,
        tokensSent: 0,
        tokensReceived: 0,
      };
      this.providers.set(provider, metrics);
    }
    return metrics;
  }

  /**
   * Get or create metrics for a rule
   */
  private getRuleMetrics(rule: string): RuleMetrics {
    let metrics = this.rules.get(rule);
    if (!metrics) {
      metrics = {
        matchCount: 0,
        lastMatchTime: null,
      };
      this.rules.set(rule, metrics);
    }
    return metrics;
  }

  /**
   * Record start of a request
   */
  recordRequestStart(): void {
    this.totalRequests++;
    this.activeRequests++;
  }

  /**
   * Record end of a request
   */
  recordRequestEnd(
    provider: string,
    success: boolean,
    latencyMs: number,
    isStreaming: boolean
  ): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);

    const metrics = this.getProviderMetrics(provider);
    metrics.totalRequests++;
    metrics.totalLatencyMs += latencyMs;

    if (success) {
      metrics.successfulRequests++;
    } else {
      metrics.failedRequests++;
    }

    if (isStreaming) {
      metrics.streamingRequests++;
    }
  }

  /**
   * Record a routing decision
   */
  recordRoutingDecision(decision: RoutingDecision): void {
    if (decision.matchedRule) {
      const metrics = this.getRuleMetrics(decision.matchedRule);
      metrics.matchCount++;
      metrics.lastMatchTime = new Date();
    }
  }

  /**
   * Record token usage
   */
  recordTokenUsage(
    provider: string,
    inputTokens: number,
    outputTokens: number
  ): void {
    const metrics = this.getProviderMetrics(provider);
    metrics.tokensSent += inputTokens;
    metrics.tokensReceived += outputTokens;
  }

  /**
   * Get all metrics
   */
  getMetrics(): GatewayMetrics {
    return {
      startTime: this.startTime,
      totalRequests: this.totalRequests,
      activeRequests: this.activeRequests,
      providers: new Map(this.providers),
      rules: new Map(this.rules),
    };
  }

  /**
   * Get metrics as a plain object for JSON serialization
   */
  toJSON(): Record<string, unknown> {
    const providerStats: Record<string, ProviderMetrics> = {};
    for (const [name, metrics] of this.providers) {
      providerStats[name] = { ...metrics };
    }

    const ruleStats: Record<string, RuleMetrics> = {};
    for (const [name, metrics] of this.rules) {
      ruleStats[name] = {
        matchCount: metrics.matchCount,
        lastMatchTime: metrics.lastMatchTime,
      };
    }

    return {
      startTime: this.startTime.toISOString(),
      uptime_seconds: Math.floor(
        (Date.now() - this.startTime.getTime()) / 1000
      ),
      totalRequests: this.totalRequests,
      activeRequests: this.activeRequests,
      providers: providerStats,
      rules: ruleStats,
    };
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.startTime = new Date();
    this.totalRequests = 0;
    this.activeRequests = 0;
    this.providers.clear();
    this.rules.clear();
  }
}

// Global metrics instance
let metricsCollector: MetricsCollector | null = null;

/**
 * Initialize the metrics collector
 */
export function initMetrics(): void {
  metricsCollector = new MetricsCollector();
}

/**
 * Get the metrics collector instance
 */
export function getMetrics(): MetricsCollector {
  if (!metricsCollector) {
    metricsCollector = new MetricsCollector();
  }
  return metricsCollector;
}
